import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import { AppThunk, RootState } from '.';
import { EntriesState, initialEntriesState, Loading } from '../utils/store';
import { productServiceLocalesClient } from '../api-client/product-service-client';
import { resolveAxiosErrorResponse } from '../utils/errors';

interface LocalesState extends EntriesState<LocaleResponse> {
  currentLocaleId?: LocaleResponse['id'];
}

export const initialLocaleState: LocalesState = {
  ...initialEntriesState,
};

const localesAdapter = createEntityAdapter<LocaleResponse>();

// thunks

export const fetchLocales = createAsyncThunk('locales/fetchLocalesStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await productServiceLocalesClient.getLocales();
    return response.data.locales;
  } catch (error) {
    // error notifications must be handled in components
    return rejectWithValue(resolveAxiosErrorResponse(error)?.statusText);
  }
});

// slice

const locales = createSlice({
  name: 'locales',
  initialState: localesAdapter.getInitialState(initialLocaleState),
  reducers: {
    addOneLocale: localesAdapter.addOne,
    removeOneLocale: localesAdapter.removeOne,
    updateCurrentLocaleId(state, action: PayloadAction<LocaleResponse['id']>) {
      state.currentLocaleId = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchLocales.pending, state => {
      state.loading = Loading.PENDING;
    });
    builder.addCase(fetchLocales.fulfilled, (state, action) => {
      localesAdapter.setAll(state, action.payload);
      state.loading = Loading.SUCCEEDED;
    });
    builder.addCase(fetchLocales.rejected, state => {
      state.loading = Loading.FAILED;
    });
  },
});

export const { addOneLocale, removeOneLocale, updateCurrentLocaleId } = locales.actions;

export default locales.reducer;

// selectors

export const { selectAll: selectAllLocales, selectById: selectLocaleById } = localesAdapter.getSelectors((state: RootState) => state.locales);

export const selectLocaleLoading = (state: RootState) => state.locales.loading;
export const selectCurrentLocaleId = (state: RootState) => state.locales.currentLocaleId;
export const selectCurrentLocale = (state: RootState) =>
  state.locales.currentLocaleId ? selectLocaleById(state, state.locales.currentLocaleId) : undefined;

export const setLocale =
  (id: LocaleResponse['id']): AppThunk =>
  dispatch => {
    dispatch(updateCurrentLocaleId(id));
  };
